import type { ErrorData } from 'hls.js'
import type { CSSProperties } from 'react'
import Hls from 'hls.js'
import { useEffect, useRef } from 'react'

type Props = {
  src: string
  poster?: string
  className?: string
  style?: CSSProperties
  onError?: (error: string) => void
}

export function VideoStreamPlayer({ src, poster, className, style, onError }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const hlsRef = useRef<Hls | null>(null)

  const retryCountRef = useRef(0)

  const MAX_RETRY_COUNT = 6 // 最大リトライ回数
  const RETRY_DELAY = 5000 // 5秒

  const handleHlsError = (data: ErrorData) => {
    const { type, details, fatal } = data

    console.error('HLS Error:', { type, details, fatal })

    if (retryCountRef.current < MAX_RETRY_COUNT) {
      retryCountRef.current++

      setTimeout(() => {
        console.log(`Retrying HLS initialization... Attempt ${retryCountRef.current}/${MAX_RETRY_COUNT}`)
        // HLSインスタンスを破棄して再作成
        hlsRef.current?.destroy()
        initializeHls()
      }, RETRY_DELAY * retryCountRef.current)
    } else {
      onError?.('再生に失敗しました。動画の処理が遅れる場合があります。時間をおいて再度お試しください。')
      console.error('Max retry count reached. Unable to recover from HLS error.')
    }
  }

  const initializeHls = () => {
    if (!videoRef.current) {
      return
    }

    const hls = new Hls({
      enableWorker: true,
      // リトライ関連の設定
      manifestLoadingTimeOut: 10000,
      manifestLoadingMaxRetry: 3,
      levelLoadingTimeOut: 10000,
      levelLoadingMaxRetry: 3,
      fragLoadingTimeOut: 20000,
      fragLoadingMaxRetry: 3,
    })

    hls.loadSource(src)
    hls.attachMedia(videoRef.current)

    // エラーハンドリング
    hls.on(Hls.Events.ERROR, (_event, data: ErrorData) => {
      handleHlsError(data)
    })

    hlsRef.current = hls
  }

  // const retry = () => {
  //   retryCountRef.current = 0
  //   setError(null)
  //   hlsRef.current?.destroy()
  //   initializeHls()
  // }

  useEffect(() => {
    const video = videoRef.current
    if (!video) {
      return
    }

    // HLS形式かどうかを判定
    const isHLS = src.includes('.m3u8')
    if (!isHLS) {
      video.src = src
      return
    }

    if (Hls.isSupported()) {
      initializeHls()
    } else if (videoRef.current?.canPlayType('application/vnd.apple.mpegurl')) {
      videoRef.current.src = src
    }

    return () => {
      hlsRef.current?.destroy()
    }
  }, [src])

  return (

    <video
      ref={videoRef}
      className={className}
      style={style}
      poster={poster}
      autoPlay
      loop
      muted
      playsInline
    />

  )
}
